import api from '../../lib/api';
import type {
  ControlledDocument,
  DocumentAcknowledgement,
  AcknowledgementAudience,
  DocumentListParams,
  DocumentListResponse,
  DocumentPayload,
  DocumentType,
  DocumentTypePayload,
  DocumentVersion,
  PendingAcknowledgementItem,
  ReadingReport,
  VersionPayload,
} from './types';

export const fetchDocumentTypes = async (activeOnly = false): Promise<DocumentType[]> => {
  const { data } = await api.get<DocumentType[]>('documentos/tipos', { params: { activeOnly } });
  return data;
};

export const createDocumentType = async (payload: DocumentTypePayload): Promise<DocumentType> => {
  const { data } = await api.post<DocumentType>('documentos/tipos', payload);
  return data;
};

export const updateDocumentType = async (
  id: string,
  payload: Partial<DocumentTypePayload>,
): Promise<DocumentType> => {
  const { data } = await api.put<DocumentType>(`documentos/tipos/${id}`, payload);
  return data;
};

export const fetchDocuments = async (
  params: DocumentListParams = {},
): Promise<DocumentListResponse> => {
  const { data } = await api.get<DocumentListResponse>('documentos', { params });
  return data;
};

export const fetchDocument = async (id: string): Promise<ControlledDocument> => {
  const { data } = await api.get<ControlledDocument>(`documentos/${id}`);
  return data;
};

export const createDocument = async (payload: DocumentPayload): Promise<ControlledDocument> => {
  const { data } = await api.post<ControlledDocument>('documentos', payload);
  return data;
};

export const updateDocument = async (
  id: string,
  payload: Partial<DocumentPayload>,
): Promise<ControlledDocument> => {
  const { data } = await api.put<ControlledDocument>(`documentos/${id}`, payload);
  return data;
};

export const markDocumentObsolete = async (id: string): Promise<ControlledDocument> => {
  const { data } = await api.post<ControlledDocument>(`documentos/${id}/obsoleto`, {});
  return data;
};

export const fetchVersions = async (documentId: string): Promise<DocumentVersion[]> => {
  const { data } = await api.get<DocumentVersion[]>(`documentos/${documentId}/versiones`);
  return data;
};

export const createVersion = async (
  documentId: string,
  payload: VersionPayload,
): Promise<DocumentVersion> => {
  const { data } = await api.post<DocumentVersion>(`documentos/${documentId}/versiones`, payload);
  return data;
};

export const submitVersionForReview = async (versionId: string): Promise<DocumentVersion> => {
  const { data } = await api.post<DocumentVersion>(
    `documentos/versiones/${versionId}/enviar-a-revision`,
    {},
  );
  return data;
};

export const reviewVersion = async (versionId: string): Promise<DocumentVersion> => {
  const { data } = await api.post<DocumentVersion>(`documentos/versiones/${versionId}/revisar`, {});
  return data;
};

export const approveVersion = async (versionId: string): Promise<DocumentVersion> => {
  const { data } = await api.post<DocumentVersion>(`documentos/versiones/${versionId}/aprobar`, {});
  return data;
};

export const putVersionInForce = async (
  versionId: string,
  options?: { effectiveAt?: string | null; audience?: AcknowledgementAudience | null },
): Promise<DocumentVersion> => {
  const { data } = await api.post<DocumentVersion>(`documentos/versiones/${versionId}/vigencia`, {
    effectiveAt: options?.effectiveAt ?? null,
    audience: options?.audience ?? null,
  });
  return data;
};

export const markVersionObsolete = async (versionId: string): Promise<DocumentVersion> => {
  const { data } = await api.post<DocumentVersion>(
    `documentos/versiones/${versionId}/obsoleto`,
    {},
  );
  return data;
};

export const acknowledgeVersion = async (versionId: string): Promise<DocumentAcknowledgement> => {
  const { data } = await api.post<DocumentAcknowledgement>(
    `documentos/versiones/${versionId}/acuse`,
    {},
  );
  return data;
};

export const fetchMyAcknowledgement = async (
  versionId: string,
): Promise<DocumentAcknowledgement | null> => {
  const { data } = await api.get<DocumentAcknowledgement | null>(
    `documentos/versiones/${versionId}/mi-acuse`,
  );
  return data;
};

export const fetchReadingReport = async (versionId: string): Promise<ReadingReport> => {
  const { data } = await api.get<ReadingReport>(
    `documentos/versiones/${versionId}/lectura-reporte`,
  );
  return data;
};

export const fetchPendingAcknowledgements = async (): Promise<PendingAcknowledgementItem[]> => {
  const { data } = await api.get<{ data: PendingAcknowledgementItem[] }>('documentos/acuse-pendientes');
  return data.data;
};

export async function downloadReadingReportCsv(versionId: string): Promise<void> {
  const response = await api.get<Blob>(`documentos/versiones/${versionId}/lectura-export.csv`, {
    responseType: 'blob',
  });
  const fallback = `informe-lectura-${versionId.slice(0, 8)}.csv`;
  const header = response.headers['content-disposition'] as string | undefined;
  const match = header ? /filename="([^"]+)"/i.exec(header) : null;
  const filename = match?.[1]?.slice(0, 255) ?? fallback;
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
